import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

const REVEAL_SELECTOR = [
  '.xsto-section-head',
  '.xsto-pathways > .xsto-pathway',
  '.xsto-model-grid > .xsto-model-card',
  '.xsto-awards__grid > .xsto-award-card',
  '.xsto-handling-grid > .xsto-handling-card',
  '.xsto-use-cases__grid > .xsto-use-case',
  '.xsto-fit-guide__layout',
  '.xsto-comparison-table-wrap',
  '.xsto-guidance__grid > .xsto-guidance-card',
  '.xsto-video-gallery',
  '.xsto-tech',
  '.xsto-service-grid > .xsto-service',
  '.xsto-support-banner',
  '.xsto-faq-list > .xsto-faq',
  '.xsto-cta__inner',
  '.xsto-info-grid > .xsto-info-card',
  '.xsto-product-hero__grid',
].join(',');

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
let observer;

function reveal(element) {
  element.classList.remove('is-pending');
  element.classList.add('is-visible');
  observer?.unobserve(element);
}

function getRootBounds(root) {
  if (root) return root.getBoundingClientRect();
  return { top: 0, bottom: window.innerHeight };
}

function isAlreadyVisible(element, root) {
  const rect = element.getBoundingClientRect();
  const rootBounds = getRootBounds(root);
  return rect.bottom > rootBounds.top && rect.top < rootBounds.bottom;
}

function siblingRevealIndex(element) {
  const parent = element.parentElement;
  if (!parent) return 0;

  // Stagger within the same grid/list so cards cascade; section heads stay at 0.
  return Array.from(parent.children).indexOf(element);
}

function initialize(scope = document) {
  if (window.Shopify?.designMode || reducedMotion.matches || !('IntersectionObserver' in window)) return;

  const root = getIntersectionRoot();
  observer ??= new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) reveal(entry.target);
      });
    },
    { root, rootMargin: '0px 0px -12% 0px', threshold: 0.1 }
  );

  scope.querySelectorAll(REVEAL_SELECTOR).forEach((element) => {
    if (element.dataset.xstoRevealInitialized === 'true') return;

    const delayIndex = Math.max(0, siblingRevealIndex(element));
    element.dataset.xstoRevealInitialized = 'true';
    element.classList.add('xsto-reveal');
    element.style.setProperty('--xsto-reveal-delay', `${Math.min(delayIndex, 5) * 110}ms`);

    if (isAlreadyVisible(element, root)) {
      element.classList.add('is-visible');
      return;
    }

    element.classList.add('is-pending');
    observer.observe(element);
  });
}

function showEverything() {
  observer?.disconnect();
  observer = undefined;
  document.querySelectorAll('.xsto-reveal').forEach((element) => {
    element.classList.remove('is-pending');
    element.classList.add('is-visible');
  });
}

function rebuildPendingObserver() {
  observer?.disconnect();
  observer = undefined;
  document.querySelectorAll('.xsto-reveal.is-pending').forEach((element) => {
    element.classList.remove('is-pending');
    delete element.dataset.xstoRevealInitialized;
  });
  initialize();
}

if (!window.Shopify?.designMode) {
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  reducedMotion.addEventListener('change', (event) => {
    if (event.matches) showEverything();
    else initialize();
  });
  scrollContainerMediaQuery.addEventListener('change', rebuildPendingObserver);
}
